<script setup lang="ts">
const props = withDefaults(defineProps<{
  rows?: number
}>(), {
  rows: 3
})

const rowCount = computed(() => Math.max(1, Math.floor(props.rows || 3)))
</script>

<template>
  <div class="comment-skeleton-list" aria-hidden="true">
    <div v-for="idx in rowCount" :key="idx" class="comment-skeleton-item">
      <div class="comment-skeleton-box card">
        <div class="comment-skeleton-avatar-wrap">
          <span class="comment-skeleton-avatar skeleton-block" />
          <span class="comment-skeleton-floor skeleton-block" />
        </div>
        <div class="comment-skeleton-main">
          <span class="comment-skeleton-head skeleton-block" />
          <span class="comment-skeleton-identity skeleton-block" />
          <div class="comment-skeleton-content">
            <span class="comment-skeleton-line line-wide skeleton-block" />
            <span class="comment-skeleton-line line-mid skeleton-block" />
            <span class="comment-skeleton-line line-short skeleton-block" />
          </div>
          <span class="comment-skeleton-ua skeleton-block" />
          <span class="comment-skeleton-reply skeleton-block" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.comment-skeleton-list {
  --skeleton-bg: color-mix(in srgb, var(--line) 74%, var(--bg) 26%);
  --skeleton-glow: color-mix(in srgb, var(--bg) 70%, var(--skeleton-bg) 30%);
  display: grid;
  gap: 12px;
}

.comment-skeleton-item {
  display: grid;
}

.comment-skeleton-box {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr);
  gap: 10px;
  align-items: start;
  padding: 14px 16px;
}

.comment-skeleton-avatar-wrap {
  display: grid;
  justify-items: center;
  gap: 6px;
}

.comment-skeleton-avatar {
  width: 40px;
  height: 40px;
  border-radius: 999px;
}

.comment-skeleton-floor {
  width: 28px;
  height: 10px;
  border-radius: 999px;
}

.comment-skeleton-main {
  min-width: 0;
  display: grid;
  gap: 8px;
}

.comment-skeleton-head {
  width: 25%;
  height: 12px;
  border-radius: 999px;
}

.comment-skeleton-identity {
  width: 48%;
  height: 12px;
  border-radius: 999px;
}

.comment-skeleton-content {
  display: grid;
  gap: 8px;
}

.comment-skeleton-line {
  height: 11px;
  border-radius: 999px;
}

.comment-skeleton-line.line-wide {
  width: 100%;
}

.comment-skeleton-line.line-mid {
  width: 82%;
}

.comment-skeleton-line.line-short {
  width: 58%;
}

.comment-skeleton-ua {
  width: 38%;
  height: 12px;
  border-radius: 999px;
}

.comment-skeleton-reply {
  width: 48px;
  height: 12px;
  border-radius: 999px;
}

.skeleton-block {
  position: relative;
  overflow: hidden;
  background: var(--skeleton-bg);
}

.skeleton-block::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(100deg, transparent 16%, var(--skeleton-glow) 50%, transparent 84%);
  animation: skeleton-shimmer 1.8s ease-in-out infinite;
}

@keyframes skeleton-shimmer {
  100% {
    transform: translateX(100%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .skeleton-block::after {
    animation: none;
  }
}
</style>
